import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { LifeBuoy, BookOpen, ExternalLink, Shield } from "lucide-react";
import { cn } from "lib/utils";
import HelpHero from "./Components/HelpHero/HelpHero";
import QuickGuides from "./Components/QuickGuides/QuickGuides";
import FaqSection from "./Components/FaqSection/FaqSection";
import ContactSupport from "./Components/ContactSupport/ContactSupport";
import styles from "./Help.module.scss";

const HelpPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Search and category states from query params
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get("q") || "");
  const [selectedCategory, setSelectedCategory] = useState(
    () => searchParams.get("category") || "all"
  );

  // Sync state to URL params cleanly
  const updateUrlParams = (newQuery, newCategory) => {
    const params = new URLSearchParams(searchParams);
    if (newQuery) {
      params.set("q", newQuery);
    } else {
      params.delete("q");
    }

    if (newCategory && newCategory !== "all") {
      params.set("category", newCategory);
    } else {
      params.delete("category");
    }

    setSearchParams(params, { replace: true });
  };

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    updateUrlParams(val, selectedCategory);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    updateUrlParams("", selectedCategory);
  };

  const handleCategorySelect = (catId) => {
    setSelectedCategory(catId);
    updateUrlParams(searchQuery, catId);
  };

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Scroll to section on initial render if specified in url (e.g. ?section=contact)
  useEffect(() => {
    const sectionTarget = searchParams.get("section");
    if (sectionTarget) {
      const timer = setTimeout(() => {
        scrollToSection(sectionTarget);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  return (
    <div className={cn(styles.helpContainer, "w-full")}>
      <div className={styles.contentWrapper}>
        {/* Top Header Breadcrumb / Meta */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-2 border-b border-gray-200/80 gap-2">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center shadow-sm">
              <LifeBuoy size={18} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">Help Center & Support</h1>
              <p className="text-xs text-gray-500">Guides, documentation, and technical support</p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5 text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              All Systems Operational
            </span>
          </div>
        </div>

        {/* Hero Banner with Search */}
        <HelpHero
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
          onScrollToSection={scrollToSection}
        />

        {/* Quick Feature Guides */}
        <QuickGuides />

        {/* FAQ Section */}
        <FaqSection
          searchQuery={searchQuery}
          selectedCategory={selectedCategory}
          onSelectCategory={handleCategorySelect}
          onClearSearch={handleClearSearch}
          onScrollToContact={() => scrollToSection("contact-support")}
        />

        {/* Contact Support Form */}
        <ContactSupport />

        {/* Help Footer */}
        <footer className="pt-6 border-t border-gray-200 text-xs text-gray-400 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>© {new Date().getFullYear()} Academic Online. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <span className="hover:text-gray-600 cursor-pointer transition-colors">Platform Status</span>
            <span>•</span>
            <span className="hover:text-gray-600 cursor-pointer transition-colors">API Documentation</span>
            <span>•</span>
            <span className="hover:text-gray-600 cursor-pointer transition-colors">Privacy Policy</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HelpPage;
